import * as dbConstants from '../../dao/dbConstants';

import * as CompanyDAO from '../../dao/CompanyDAO';
import * as FirmDAO from '../../dao/FirmDAO';
import * as AssetDAO from '../../dao/AssetDAO';
import * as KnowledgeBaseDAO from '../../dao/KnowledgeBaseDAO';
import * as PersonaDAO from '../../dao/PersonaDAO';
import * as ProductDAO from '../../dao/ProductDAO';
import * as TopicDAO from '../../dao/TopicDAO';
import * as WritingStyleDAO from '../../dao/WritingStyleDAO';
import * as BrandVoiceDAO from '../../dao/BrandVoiceDAO';
import PermissionsTable from '../permissions/PermissionsTable';

import { getFileDownloadURL, getTimeZonesLookupList, getDateFormatLookupList } from '../../utils/apiUtils';
import { isValidEmail, isValidURL, isValidZIPCode, isValidYoutubeSearch } from '../../utils/validationUtils';
import { evaluateCondition, getFileName } from '../../utils/utils'

import { DATA_TYPE_INTEGER, DATA_TYPE_STRING, INPUT_LAYOUT_TYPE_INLINE, 
  INPUT_LAYOUT_TYPE_SEPARATE_LABEL, INPUT_TYPE_CHECKBOX, INPUT_TYPE_FILE,
  INPUT_TYPE_LINK, INPUT_TYPE_QUESTIONS, INPUT_TYPE_RADIO_GROUP, 
  INPUT_TYPE_SELECT_MULTI, INPUT_TYPE_SELECT_SINGLE, INPUT_TYPE_TEXT_AREA,
  INPUT_TYPE_TRIPADVISOR,
  INPUT_TYPE_CUSTOM_COMPONENT,
  } from '../componentConstants';

const layoutType = INPUT_LAYOUT_TYPE_SEPARATE_LABEL;

const BRAND_VOICE_CONFIG = {
  sections: [
    {
      heading: '',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 100 }
        },
        { fieldName: 'description', label: 'Description', tooltipText: '', layoutType,
          inputType: INPUT_TYPE_TEXT_AREA, validationRules: { maxLength: 1000 }
        },
      ],
    },
    {
      heading: '',
      inputs: [
        {
          fieldName: 'questionAnswers', inputType: INPUT_TYPE_QUESTIONS, layoutType,
          subTypeIdAttributeName: 'dummyId', // this will be sent as typeId to the server side.
        },
      ],
    }
  ],
};

const CAMPAIGN_CONFIG = {
  sections: [
    {
      heading: '',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 100 }
        },
        { fieldName: 'description', label: 'Description', inputType: INPUT_TYPE_TEXT_AREA, layoutType,
          validationRules: { isRequired: false, maxLength: 1000 } 
        },
        { fieldName: 'companyId', label: 'Company', inputType: INPUT_TYPE_SELECT_SINGLE, layoutType,
          optionsFunc: CompanyDAO.getListOptions, optionsEntityName: dbConstants.ENTITY_NAME_COMPANY,
          changeReaction: {
            entityName: dbConstants.ENTITY_NAME_COMPANY,
            // INFO - Make sure that dependent field is mentioned after the dependency e.g. companyId before productId.
            fields: { brandVoiceId: 'brandVoiceId', knowledgeBaseIds: 'knowledgeBaseIds', assetIds: 'assetIds' }
          },
          validationRules: { isRequired: true }
        },
        { fieldName: 'productId', label: 'Product', inputType: INPUT_TYPE_SELECT_SINGLE, layoutType,
          // Function to call to get the select list options.
          optionsFunc: ProductDAO.getListOptions, optionsEntityName: dbConstants.ENTITY_NAME_PRODUCT,
          // Input to the options getter function
          optionsFuncInput: {companyId: null},
          // Name of the attribute in the form, this form field depends upon.
          dependsOn: 'companyId',
          // Show only when the dependsOn field value is not empty
          showWithDependency: true
        },
        { fieldName: 'personaId', label: 'Persona', inputType: INPUT_TYPE_SELECT_SINGLE, layoutType,
          optionsFunc: PersonaDAO.getListOptions, optionsEntityName: dbConstants.ENTITY_NAME_PERSONA,
        },
        { fieldName: 'brandVoiceId', label: 'Brand Voice', inputType: INPUT_TYPE_SELECT_SINGLE, layoutType,
          optionsFunc: BrandVoiceDAO.getListOptions, optionsEntityName: dbConstants.ENTITY_NAME_BRAND_VOICE,
        },
        { fieldName: 'assetIds', label: 'Trust Signals', inputType: INPUT_TYPE_SELECT_MULTI, layoutType,
          optionsFunc: AssetDAO.getListOptions, optionsEntityName: dbConstants.ENTITY_NAME_ASSET,
        },
        { fieldName: 'knowledgeBaseIds', label: 'Knowledge Bases', placeholder: 'Select Knowledge Bases',inputType: INPUT_TYPE_SELECT_MULTI, layoutType,
          optionsFunc: KnowledgeBaseDAO.getListOptions, optionsEntityName: dbConstants.ENTITY_NAME_KNOWLEDGE_BASE,
        },
      ],
    },
  ],
};

const FIRM_CONFIG = {
  sections: [
    {
      heading: '',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 250 }
        },
        { fieldName: 'defaultLanguage', label: 'Default Language', inputType: INPUT_TYPE_SELECT_SINGLE,
          validationRules: { isRequired: true }, dataType: DATA_TYPE_STRING,
          layoutType, optionsFunc: FirmDAO.getLanguageListOptions,
          optionsEntityName: dbConstants.ENTITY_NAME_LANGUAGE,
        },
        { fieldName: 'timezone', label: 'Time Zone', inputType: INPUT_TYPE_SELECT_SINGLE,
          validationRules: { isRequired: true }, dataType: DATA_TYPE_STRING,
          layoutType, optionsFunc: getTimeZonesLookupList,
          optionsEntityName: dbConstants.ENTITY_NAME_FIRM,
        },
        { fieldName: 'dateFormat', label: 'Date Format', inputType: INPUT_TYPE_SELECT_SINGLE,
          validationRules: { isRequired: true }, dataType: DATA_TYPE_STRING,
          layoutType, optionsFunc: getDateFormatLookupList,
          optionsEntityName: dbConstants.ENTITY_NAME_FIRM,
        }
      ],
    },
  ],
};

const KNOLEDGE_BASE_CONFIG = {
  sections: [
    {
      heading: '',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 100 } 
        },
      ],
    },
  ],
};

const PERSONA_CONFIG = {
  sections: [
    {
      heading: '',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 100 }
        },
        { fieldName: 'description', label: 'Description', tooltipText: '', layoutType,
          inputType: INPUT_TYPE_TEXT_AREA, validationRules: { maxLength: 1000 }
        },
      ],
    },
    {
      heading: '',
      inputs: [
        {
          fieldName: 'questionAnswers', inputType: INPUT_TYPE_QUESTIONS, layoutType,
          subTypeIdAttributeName: 'dummyId', // this will be sent as typeId to the server side.
        },
      ],
    },
  ],
};

const TOPIC_CONFIG = {
  sections: [
    {
      heading: '',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 250 }
        },
        { fieldName: 'parentId', label: 'Category', inputType: INPUT_TYPE_SELECT_SINGLE,
          tooltipText: '', layoutType, optionsFunc: TopicDAO.getAllListOptions,
          optionsEntityName: dbConstants.ENTITY_NAME_TOPIC
        },
        { fieldName: 'isActive', label: 'Active?', inputType: INPUT_TYPE_CHECKBOX, layoutType:INPUT_LAYOUT_TYPE_INLINE },
      ],
    },
  ],
};

const WRITING_STYLE_CONFIG = {
  sections: [
    {
      heading: '',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 100 }
        },
        { fieldName: 'type', label: 'Type', inputType: INPUT_TYPE_SELECT_SINGLE,
          validationRules: { isRequired: true }, dataType: DATA_TYPE_STRING,
          layoutType, optionsFunc: WritingStyleDAO.getTypeListOptions,
          optionsEntityName: dbConstants.ENTITY_NAME_WRITING_STYLE_TYPE,
          isDisabled: (record) => (!!record.id),
        },
        { fieldName: 'documentSrc', label: 'URL', tooltipText: '', layoutType,
          validationRules: { isURL: true, maxLength: 250 },
          isHidden: (record) => (record.type !== dbConstants.WRITING_STYLE_TYPE_LINK),
          isRequired: (record) => (record.type === dbConstants.WRITING_STYLE_TYPE_LINK),
          isDisabled: (record) => (!!record.id && record.type === dbConstants.WRITING_STYLE_TYPE_LINK),
        },
        { fieldName: 'file', label: 'File', inputType: INPUT_TYPE_FILE,
          tooltipText: '', layoutType, fileNameField: 'documentSrc',
          acceptedFileTypes: "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,text/plain,text/csv",
          acceptedFileTypelabels:"doc, docx, pdf, csv or txt",
          isHidden: (record) => (!!record.id || record.type !== dbConstants.WRITING_STYLE_TYPE_FILE),
          isRequired: (record) => (record.type === dbConstants.WRITING_STYLE_TYPE_FILE),
        },
        { linkedLabel: 'View Website', inputType: INPUT_TYPE_LINK, layoutType, hrefField: 'documentSrc', target: '_blank',
          isHidden: (record) => (!record.id || record.type !== dbConstants.WRITING_STYLE_TYPE_LINK),
          iconClassName: 'bx bx-right-top-arrow-circle'
        },
        { linkedLabel: (record) => (getFileName(record.documentSrc)), inputType: INPUT_TYPE_LINK, layoutType,
          isHidden: (record) => (!record.id || record.type !== dbConstants.WRITING_STYLE_TYPE_FILE),
          onClick:async (record) => {
            const fileDownloadURL = await getFileDownloadURL(dbConstants.ENTITY_NAME_WRITING_STYLE, record?.id)
            window.open(fileDownloadURL, "_blank");
          },
          iconClassName: 'bx bx-download',
        },
      ],
    },
  ],
}

const ROLE_CONFIG = {
  sections: [
    {
      heading: 'Role Details',
      inputs: [
        { fieldName: 'name', label: 'Name', tooltipText: '', layoutType,
          validationRules: { isRequired: true, maxLength: 100 }
        },
        { fieldName: 'description', label: 'Description', inputType: INPUT_TYPE_TEXT_AREA, layoutType,
          validationRules: { isRequired: false, maxLength: 250 } 
        },
      ],
    },
    {
      heading: 'Permissions',
      inputs: [
        { 
          fieldName: 'permissionIds', 
          label: '', 
          inputType: INPUT_TYPE_CUSTOM_COMPONENT,
          layoutType,
          componentRef: PermissionsTable,
          changeHandlerPropName: 'onChange', 
          componentInput: {
            fieldName: 'permissionIds',
          }
        }
      ]
    }
  ],
};

const allInputConfigs = {
  [dbConstants.ENTITY_NAME_BRAND_VOICE]: BRAND_VOICE_CONFIG,
  [dbConstants.ENTITY_NAME_CAMPAIGN]: CAMPAIGN_CONFIG,
  [dbConstants.ENTITY_NAME_FIRM]: FIRM_CONFIG,
  [dbConstants.ENTITY_NAME_KNOWLEDGE_BASE]: KNOLEDGE_BASE_CONFIG,
  [dbConstants.ENTITY_NAME_PERSONA]: PERSONA_CONFIG,
  [dbConstants.ENTITY_NAME_TOPIC]: TOPIC_CONFIG,
  [dbConstants.ENTITY_NAME_WRITING_STYLE]: WRITING_STYLE_CONFIG,
  [dbConstants.ENTITY_NAME_ROLE]: ROLE_CONFIG,
};

export const getInputConfig = (entityName) => {
  return allInputConfigs[entityName];
};

export const removeHiddenAttributes = (entityName, inputConfig, record) => {
  console.log('removeHiddenAttributes - Removing the hidden attribute values from the', entityName, ' object');
  if (!inputConfig) throw new Error('Input configuration not found.');
  const effectiveAttributes = {};
  inputConfig.sections.forEach(section => {
    section.inputs.forEach(input => {
      const isHidden = evaluateCondition(input?.isHidden, record);
      effectiveAttributes[input.fieldName] = (effectiveAttributes[input.fieldName] || !isHidden);
    });
  });

  Object.keys(record).forEach(key => {
    if(effectiveAttributes[key] === false) {
      console.log('removeHiddenAttributes - Removing attribute', key);
      delete record[key];
    }
  });

  return record;
};

export const getFileFields = (entityName, inputConfig, record) => {
  console.log('getFileFields - Checking for any FILE type input fields for the entity', entityName);
  if (!inputConfig) throw new Error('Input configuration not found.');
  const fileFields = [];
  inputConfig.sections.forEach(section => {
    section.inputs.forEach(input => {
      if(input.inputType === INPUT_TYPE_FILE) {
        console.log('getFileFields - FILE type field:', input);
        const {fieldName, fileNameField} = input;
        fileFields.push({fieldName, fileNameField, ignore: evaluateCondition(input?.isHidden, record)}); //set to ignore=true if file control is hidden so that it can be delete from the record before saving.
      }
    });
  });
  console.log('getFileFields - Returning', fileFields);
  return fileFields;
};

const validateFileField = (input, record) => {
  if(input?.inputType !== INPUT_TYPE_FILE)  return null;
 
  const rules = input.validationRules;
  const isRequired = rules?.isRequired || evaluateCondition(input?.isRequired, record);

  const file = record[input.fieldName];

  const fieldLabel = input.label;
  if (!file) {
    if (!isRequired) return null;
    return {shortMessage: 'Required', fullMessage: `${fieldLabel} is required`};
  }

  if(!input.acceptedFileTypes) return null;

  if(file instanceof File && input.acceptedFileTypes) {
    const fileType = file?.type;
    const isFileTypeAllowed = input.acceptedFileTypes?.split(',').some(type => type === fileType);

    if (!isFileTypeAllowed) {
      return {shortMessage: `File type is not allowed`, fullMessage: `Selected file type is not allowed`};
    }
  }
}

const validateField = (input, record) => {
  if(input?.inputType === INPUT_TYPE_FILE) {
    return validateFileField(input, record);
  }

  if(input?.inputType === INPUT_TYPE_RADIO_GROUP) {
    const isRequired = evaluateCondition(input?.isRequired, record);
    const fieldLabel = input.label;
    const value = record[input.fieldName];
    
    if (isRequired && (value === undefined || value === null || value === '')) {
      return {shortMessage: 'Required', fullMessage: `${fieldLabel} is required`};
    }
    return '';
  }

  if(input?.inputType === INPUT_TYPE_TRIPADVISOR) {
    const isRequired = evaluateCondition(input?.isRequired, record);
    
    // For TripAdvisor, check if locationId is set (which means a location has been selected)
    // Don't show error if article type is not TripAdvisor yet
    if (isRequired && record.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR && !record.locationId) {
      return {shortMessage: 'Location Required', fullMessage: `Please select a location from the search results`};
    }
    return '';
  }

  const rules = input.validationRules || {};
  const isRequired = rules?.isRequired || evaluateCondition(input?.isRequired, record);

  const fieldLabel = input.label;
  let value = record[input.fieldName];

  if (!rules && !isRequired && input.dataType !== DATA_TYPE_INTEGER) return '';

  if(typeof value === 'string') {
    value = value.trim();
  }
  if (isRequired && !value) {
    return {shortMessage: 'Required', fullMessage: `${fieldLabel} is required`};
  }
  if (rules.minLength && value && value.length < rules.minLength) {
    return {shortMessage: `Must be at least ${rules.minLength} characters long`, fullMessage: `${fieldLabel} mst be at least ${rules.minLength} characters long`};
  }
  if (rules.maxLength && value && value.length > rules.maxLength) {
    return {shortMessage: `Must be at most ${rules.maxLength} characters long`, fullMessage: `${fieldLabel} must be at most ${rules.maxLength} characters long`};
  }
  if (input.dataType === DATA_TYPE_INTEGER || rules.minValue || rules.maxValue) {
    const regex = /^-?\d*(\.\d*)?$/;
    const isNumber = regex.test(value);
    if(!isNumber) return {shortMessage: `Must be a number`, fullMessage: `${fieldLabel} mst be a number`};

    try {
      value = parseFloat(value);
    }
    catch {
      return {shortMessage: 'Must be a number', fullMessage: `${fieldLabel} must be a number`};
    }
    if (rules.minValue && value < rules.minValue) {
      return {shortMessage: `Must not be less than ${rules.minValue}`, fullMessage: `${fieldLabel} must not be less than ${rules.minValue}`};
    }
    if (rules.maxValue && value > rules.maxValue) {
      return {shortMessage: `Must not be more than ${rules.maxValue}`, fullMessage: `${fieldLabel} must not be more than ${rules.maxValue}`};
    }
  }
  if(!value) {
    return;
  }
  if (rules.isEmail && !isValidEmail(value)) {
    return {shortMessage: 'Invalid', fullMessage: `${fieldLabel} is invalid`};
  }
  if (rules.isURL && !isValidURL(value)) {
    return {shortMessage: 'Invalid', fullMessage: `${fieldLabel} is invalid`};
  }
  if (rules.isZIPCode && !isValidZIPCode(value)) {
    return {shortMessage: 'Invalid', fullMessage: `${fieldLabel} is invalid`};
  }
  if (rules.isYoutubeSearch && !isValidYoutubeSearch(value)) {
    return {shortMessage: 'Invalid', fullMessage: `${fieldLabel} is invalid`};
  }

  // INFO - Add the minDate and maxDate checks when required

  return '';
};

export const validateRecord = (inputConfig, record) => {
  if (!inputConfig) throw new Error('Input configuration not found.');
  const errors = {};
  inputConfig.sections.forEach(section => {
    section.inputs.forEach(input => {
      if(!evaluateCondition(input?.isHidden, record)) {
        const errorMessage = validateField(input, record);
        if(!!errorMessage) errors[input.fieldName] = errorMessage;
      }
    });
  });
  return errors;
};


